import React, { useState, useEffect, useCallback } from 'react';
import { FlowingLines } from '../ui/FlowingLines';
import { dbService } from '../../services/DatabaseService';

// QuickStat Component for Dashboard Stats
function QuickStat({ icon, label, value, color }) {
    const colorClasses = {
        blue: 'from-blue-500 to-blue-600 text-blue-600',
        purple: 'from-purple-500 to-purple-600 text-purple-600',
        emerald: 'from-emerald-500 to-emerald-600 text-green-600'
    };
    
    return (
        <div className="relative group">
            <div className="bg-white/95 dark:bg-slate-800/95 backdrop-blur-md rounded-3xl p-6 border-2 border-slate-300/60 dark:border-slate-600/80 shadow-xl hover:shadow-2xl dark:shadow-2xl dark:shadow-black/50 dark:hover:shadow-black/70 transition-all duration-300 group-hover:scale-105">
                <div className={`inline-flex p-3 rounded-xl bg-linear-to-br ${colorClasses[color]} text-white mb-3 shadow-lg dark:shadow-xl dark:shadow-black/50`}>
                    {icon}
                </div>
                <div className="space-y-1">
                    <p className="text-2xl font-bold text-slate-800 dark:text-slate-100">{value}</p>
                    <p className="text-sm text-slate-600 dark:text-slate-400">{label}</p>
                </div>
                <div className="absolute inset-0 bg-linear-to-r from-transparent via-white/15 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-3xl"></div>
            </div>
        </div>
    );
}

// Modern Dashboard Cards with Gradients and Animations
function DashboardCard({ title, description, icon, onClick, isPrimary = false, isDisabled = false, gradient = "from-blue-500 to-purple-600" }) {
    if (isDisabled) {
        return (
            <div className="relative group w-full">
                <div className="bg-white/95 dark:bg-slate-800/95 backdrop-blur-md rounded-3xl p-6 border-2 border-slate-300/60 dark:border-slate-600/80 shadow-xl hover:shadow-2xl dark:shadow-2xl dark:shadow-black/50 dark:hover:shadow-black/70 transition-all duration-300 opacity-60 cursor-not-allowed min-h-[280px] max-w-sm mx-auto flex flex-col">
                    {/* Icon */}
                    <div className={`inline-flex items-center justify-center w-14 h-14 rounded-xl bg-linear-to-br ${gradient} text-white mb-4 shadow-lg dark:shadow-xl dark:shadow-black/50 opacity-70 self-center`}>
                        {icon}
                    </div>
                    
                    {/* Content */}
                <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-2">{title}</h3>
                <p className="text-sm text-slate-600 dark:text-slate-400 mb-4 grow">{description}</p>
                
                {/* Status */}
                <div className="flex items-center justify-between mt-auto">
                        <div className="flex items-center text-slate-400 text-sm font-medium">
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            In Entwicklung
                        </div>
                        <span className="text-xs text-slate-400 bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded-full">
                            Bald verfügbar
                        </span>
                    </div>
                    
                    {/* Hover Shine Effect */}
                    <div className="absolute inset-0 bg-linear-to-r from-transparent via-white/15 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-3xl"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="relative group w-full">
            <div 
                className="bg-white/95 dark:bg-slate-800/95 backdrop-blur-md rounded-3xl p-6 border-2 border-slate-300/60 dark:border-slate-600/80 shadow-xl hover:shadow-2xl dark:shadow-2xl dark:shadow-black/50 dark:hover:shadow-black/70 transition-all duration-300 group-hover:scale-105 cursor-pointer min-h-[280px] max-w-sm mx-auto flex flex-col"
                onClick={onClick}
            >
                {/* Icon */}
                <div className={`inline-flex items-center justify-center w-14 h-14 rounded-xl bg-linear-to-br ${gradient} mb-4 shadow-lg dark:shadow-xl dark:shadow-black/50 group-hover:shadow-xl dark:group-hover:shadow-2xl dark:group-hover:shadow-black/70 transform group-hover:scale-110 transition-all duration-300 self-center text-white`}>
                    {icon}
                </div>
                
                {/* Content */}
                <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-2 group-hover:text-slate-900 dark:group-hover:text-white transition-colors">
                    {title}
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-400 mb-4 grow">
                    {description}
                </p>
                
                {/* Action */}
                <div className="flex items-center justify-between mt-auto">
                    <div className={`flex items-center text-sm font-medium bg-linear-to-r ${gradient} bg-clip-text text-transparent group-hover:scale-105 transition-transform duration-200`}>
                        {isPrimary ? "Jetzt starten" : "Öffnen"}
                        <svg className="w-4 h-4 ml-1 transform group-hover:translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                    </div>
                </div>
                
                {/* Hover Shine Effect */}
                <div className="absolute inset-0 bg-linear-to-r from-transparent via-white/15 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-3xl"></div>
            </div>
        </div>
    );
}

// Modern Dashboard with Dynamic Design
export function HRDashboard({ navigateTo }) {
    const [stats, setStats] = useState({
        questionnaires: 0,
        submissions: 0,
        analyses: 0
    });
    const [isLoading, setIsLoading] = useState(true);

    // Load dashboard statistics with PocketBase subscription
    const loadStats = useCallback(async () => {
        try {
            const [questionnaires, submissionStats] = await Promise.all([
                dbService.getQuestionnairesForList(),
                dbService.getSubmissionStats()
            ]);

            setStats({
                questionnaires: questionnaires.length,
                submissions: submissionStats.total,
                analyses: submissionStats.analyses
            });
        } catch (error) {
            console.error('Error loading dashboard stats:', error);
            setStats({
                questionnaires: 0,
                submissions: 0,
                analyses: 0
            });
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Set up PocketBase subscriptions and load initial stats
    useEffect(() => {
        // Load initial stats
        loadStats();

        // Temporarily disable realtime subscriptions due to CORS issues
        // TODO: Re-enable once CORS is configured on PocketBase server
        /*
        // Subscribe to submissions changes to update stats
        pb.collection('submissions').subscribe('*', (e) => {
            console.log('Dashboard subscription event:', e.action, e.record);
            
            // Reload stats when submissions change
            loadStats();
        });

        // Cleanup subscription on unmount
        return () => {
            pb.collection('submissions').unsubscribe('*');
        };
        */
    }, [loadStats]);

    return (
        <div className="min-h-screen py-8 relative overflow-hidden">
            {/* Dynamic Flowing Lines */}
            <FlowingLines theme="emerald" opacity="60" />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                {/* Hero Section */}
                <div className="text-center mb-12">
                    <div className="relative inline-block">
                        <h1 className="text-4xl md:text-6xl font-bold rainbow-headline mb-4">
                            Willkommen zurück!
                        </h1>
                    </div>
                    <p className="text-xl text-slate-600 dark:text-slate-300 mb-8 whitespace-nowrap">
                        Verwalte deine HR-Prozesse mit KI-gestützter Intelligenz und modernem Design.
                    </p>
                    
                    {/* Quick Stats */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-12 max-w-4xl mx-auto">
                        <QuickStat 
                            icon={<svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true" role="img"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>}
                            label="Fragebögen"
                            value={isLoading ? '...' : stats.questionnaires}
                            color="blue"
                        />
                        <QuickStat 
                            icon={<svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true" role="img"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>}
                            label="Bewerbungen"
                            value={isLoading ? '...' : stats.submissions}
                            color="purple"
                        />
                        <QuickStat 
                            icon={<svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true" role="img"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>}
                            label="KI-Analysen"
                            value={isLoading ? '...' : stats.analyses}
                            color="emerald"
                        />
                    </div>
                    
                    {/* Main Row - 4 Action Cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8 max-w-6xl mx-auto">
                        <DashboardCard 
                            title="Neuen Fragebogen erstellen" 
                            description="Entwirf einen neuen Fragebogen für Bewerber mit KI-Unterstützung." 
                            icon={<svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true" role="img"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>}
                            onClick={() => navigateTo('questionnaire-editor')} 
                            isPrimary={true}
                            gradient="from-blue-500 to-purple-600"
                        />
                        <DashboardCard 
                            title="Ergebnisse & Auswertung" 
                            description="Sieh dir alle Bewerbungen und deren KI-gestützte Auswertungen an." 
                            icon={<svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true" role="img"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>}
                            onClick={() => navigateTo('results')}
                            gradient="from-emerald-500 to-teal-600"
                        />
                        <div className="relative group w-full">
                            <div 
                                className="bg-white/95 dark:bg-slate-800/95 backdrop-blur-md rounded-3xl p-6 border-2 border-slate-300/60 dark:border-slate-600/80 shadow-xl hover:shadow-2xl dark:shadow-2xl dark:shadow-black/50 dark:hover:shadow-black/70 transition-all duration-300 group-hover:scale-105 cursor-pointer min-h-[280px] max-w-sm mx-auto flex flex-col"
                                onClick={() => navigateTo('bewerbungspool')}
                            >
                                {/* Icon with FORCED dark background */}
                                <div 
                                    className="inline-flex items-center justify-center w-14 h-14 rounded-xl mb-4 shadow-lg dark:shadow-xl dark:shadow-black/50 group-hover:shadow-xl dark:group-hover:shadow-2xl dark:group-hover:shadow-black/70 transform group-hover:scale-110 transition-all duration-300 self-center text-white"
                                    style={{ background: 'linear-gradient(135deg, #1f2937, #111827)' }}
                                >
                                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true" role="img">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                    </svg>
                                </div>
                                
                                {/* Content */}
                                <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-2 group-hover:text-slate-900 dark:group-hover:text-white transition-colors">
                                    Bewerbungspool
                                </h3>
                                <p className="text-sm text-slate-600 dark:text-slate-400 mb-4 grow">
                                    Verwalte eingehende Bewerbungen aus Landing Pages und Kampagnen.
                                </p>
                                
                                {/* Action */}
                                <div className="flex items-center justify-between mt-auto">
                                    <div className="flex items-center text-sm font-medium text-slate-700 dark:text-slate-200 group-hover:scale-105 transition-transform duration-200">
                                        Öffnen
                                        <svg className="w-4 h-4 ml-1 transform group-hover:translate-x-1 transition-transform duration-200 text-slate-700 dark:text-slate-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </div>
                                </div>
                                
                                {/* Hover Shine Effect */}
                                <div className="absolute inset-0 bg-linear-to-r from-transparent via-white/15 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-3xl"></div>
                            </div>
                        </div>
                        <DashboardCard 
                            title="Einstellungen" 
                            description="Pass die KI an deine Firmenwerte und Anforderungen an." 
                            icon={<svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true" role="img"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>}
                            onClick={() => navigateTo('settings')}
                            gradient="from-orange-500 to-red-600"
                        />
                    </div>

                    {/* Bottom Card - Statistiken */}
                    <div className="flex justify-center mb-12 max-w-sm mx-auto">
                        <DashboardCard
                            title="Statistiken"
                            description="Analysiere die Ergebnisse und Trends deiner Fragebögen."
                            icon={<svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true" role="img"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>}
                            onClick={() => navigateTo('statistiken')}
                            gradient="from-cyan-500 to-blue-600"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
